import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./baseQuery";
import { OrgChartNodeData } from "../../types/org-chart";

/** Backend envelope from ResponseInterceptor */
interface IApiEnvelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

export const orgChartApi = createApi({
  reducerPath: "orgChartApi",
  baseQuery,
  tagTypes: ["OrgChart"],

  endpoints: (builder) => ({
    // ─────────────────────────────────────────────
    // GET ORG CHART
    // ─────────────────────────────────────────────
    getOrgChart: builder.query<OrgChartNodeData[], void>({
      query: () => ({
        url: "/v2/users/org-chart",
        method: "GET",
      }),
      transformResponse: (raw: IApiEnvelope<OrgChartNodeData[]>): OrgChartNodeData[] =>
        raw?.data ?? [],
      providesTags: ["OrgChart"],
    }),

    getOrgChartContext: builder.query<OrgChartNodeData[], string>({
      query: (id) => ({
        url: `/v2/users/org-chart/${id}/context`,
        method: "GET",
      }),
      transformResponse: (raw: IApiEnvelope<OrgChartNodeData[]>): OrgChartNodeData[] =>
        raw?.data ?? [],
      providesTags: (_result, _error, id) => [{ type: "OrgChart", id }],
    }),

    // ─────────────────────────────────────────────
    // REASSIGN EMPLOYEE
    // ─────────────────────────────────────────────
    reassignEmployee: builder.mutation<
      void,
      { employeeId: string; newManagerId: string | null; type: 'reporting' | 'functional' }
    >({
      query: ({ employeeId, newManagerId, type }) => ({
        url: `/v2/users/${employeeId}/reassign`,
        method: "POST",
        body: {
          [type === 'reporting' ? 'reportingManagerId' : 'functionalManagerId']: newManagerId,
        },
      }),
      invalidatesTags: ["OrgChart"],
    }),
 
    // ─────────────────────────────────────────────
    // PENDING UPDATES
    // ─────────────────────────────────────────────
    getPendingOrgChartUpdates: builder.query<any[], void>({
      query: () => ({
        url: "/v2/users/org-chart/updates/pending",
        method: "GET",
      }),
      transformResponse: (raw: IApiEnvelope<any[]>): any[] => raw?.data ?? [],
      providesTags: ["OrgChart"],
    }),
 
    actOnOrgChartUpdate: builder.mutation<
      void,
      { id: string; decision: "approved" | "rejected"; remarks?: string }
    >({
      query: ({ id, decision, remarks }) => ({
        url: `/v2/users/org-chart/update/${id}/action`,
        method: "PUT",
        body: { decision, remarks },
      }),
      invalidatesTags: ["OrgChart"],
    }),
 
    getOrgChartUpdateHistory: builder.query<any[], void>({
      query: () => ({
        url: "/v2/users/org-chart/updates/history",
        method: "GET",
      }),
      transformResponse: (raw: IApiEnvelope<any[]>): any[] => raw?.data ?? [],
      providesTags: ["OrgChart"],
    }),
  }),
});

export const {
  useGetOrgChartQuery,
  useGetOrgChartContextQuery,
  useReassignEmployeeMutation,
  useGetPendingOrgChartUpdatesQuery,
  useGetOrgChartUpdateHistoryQuery,
  useActOnOrgChartUpdateMutation,
} = orgChartApi;
